import type {
  PublicForm,
  FormFormData,
  MarketingForm,
  FormAnalytics,
  FormSubmission,
  ConversionFunnel,
  FieldCompletionStats,
} from '@/types/marketing/form.types';
import type { PaginatedResponse } from '@/types/entities';

import { ApiService } from './api.service';
import { endpoints } from './endpoints';

interface GetFormsParams {
  page?: number;
  pageSize?: number;
  search?: string;
  status?: string;
}

interface GetSubmissionsParams {
  page?: number;
  pageSize?: number;
  filter?: Record<string, string>;
}

interface DateRangeParams {
  startDate: string;
  endDate: string;
}

interface EmbedScriptResponse {
  script: string;
  type: string;
  instructions: string;
}

/**
 * Marketing forms API service
 */
export class MarketingFormApiService extends ApiService {
  /**
   * Get all forms with pagination and filters
   */
  getForms = async (params?: GetFormsParams): Promise<PaginatedResponse<MarketingForm>> =>
    this.get(endpoints.marketingForms.all, { params });

  /**
   * Get form by ID
   */
  getFormById = async (id: string): Promise<MarketingForm> =>
    this.get(endpoints.marketingForms.byId(id));

  /**
   * Create new form
   */
  createForm = async (data: FormFormData): Promise<{ id: string }> =>
    this.post(endpoints.marketingForms.create, data);

  /**
   * Update form
   */
  updateForm = async (id: string, data: Partial<FormFormData>): Promise<{ id: string }> =>
    this.put(endpoints.marketingForms.update(id), data);

  /**
   * Delete form
   */
  deleteForm = async (id: string): Promise<{ id: string }> =>
    this.delete(endpoints.marketingForms.delete(id));

  /**
   * Get embed script
   */
  getEmbedScript = async (
    id: string,
    type: 'iframe' | 'vanilla' | 'react' = 'iframe',
  ): Promise<EmbedScriptResponse> => this.get(endpoints.marketingForms.embedScript(id, type));

  /**
   * Get form submissions
   */
  getSubmissions = async (
    id: string,
    params?: GetSubmissionsParams,
  ): Promise<PaginatedResponse<FormSubmission>> =>
    this.get(endpoints.marketingForms.submissions(id), { params });

  /**
   * Get form analytics
   */
  getAnalytics = async (id: string, dateRange?: DateRangeParams): Promise<FormAnalytics> =>
    this.get(endpoints.marketingForms.analytics(id), { params: dateRange });

  /**
   * Get field completion rates
   */
  getFieldCompletion = async (
    id: string,
    dateRange?: DateRangeParams,
  ): Promise<FieldCompletionStats> =>
    this.get(endpoints.marketingForms.fieldCompletion(id), { params: dateRange });

  /**
   * Get conversion funnel
   */
  getConversionFunnel = async (
    id: string,
    dateRange?: DateRangeParams,
  ): Promise<ConversionFunnel> =>
    this.get(endpoints.marketingForms.conversionFunnel(id), { params: dateRange });

  /**
   * Get public form by slug (no auth required)
   */
  getPublicFormBySlug = async (slug: string): Promise<PublicForm> =>
    this.get(endpoints.marketingForms.publicFormBySlug(slug));

  /**
   * Submit public form data (no auth required)
   */
  submitPublicForm = async (slug: string, data: Record<string, unknown>): Promise<void> =>
    this.post(endpoints.marketingForms.publicSubmit(slug), data);
}

// Export a singleton instance
export const marketingFormApiService = new MarketingFormApiService();

// Named exports for convenience
export const {
  getForms,
  getFormById,
  createForm,
  updateForm,
  deleteForm,
  getEmbedScript,
  getSubmissions,
  getAnalytics,
  getFieldCompletion,
  getConversionFunnel,
  getPublicFormBySlug,
  submitPublicForm,
} = marketingFormApiService;
